import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {rxResource} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {TableModule} from 'primeng/table';
import {Tooltip} from 'primeng/tooltip';
import {ModelFieldSelectComponent} from '../../inputs/model-field/model-field-select/model-field-select.component';
import {ModelFieldTextComponent} from '../../inputs/model-field/model-field-text/model-field-text.component';
import {filterToolItems} from './invitation-table-quick-filter.utils';
import {
  BaseParticipatingTool,
  CurrentPhaseStatus,
  emptyInvitationTableData,
  InvitationTableConfig,
  InvitationTableData,
  PhaseInvitationConfig,
  InvitationTableQuickFilterSelectOption,
} from './invitation-table.types';
import {
  ParticipatingToolControllerService,
  ParticipatingToolSelectionRequest,
  ProjectCharterControllerService,
  TenderPhaseDetailsResponse,
  TenderSelection,
  TenderStatus,
} from '@frontend/shared-api';
import {catchError, EMPTY, firstValueFrom, from, map, of} from 'rxjs';
import {TimeframeDisplayComponent} from './components/timeframe-display/timeframe-display.component';
import {TimeframeDisplayVM} from './components/timeframe-display/timeframe-display.types';
import {ParticipatingToolSelectionComponent} from './components/columns/participating-tool-selection/participating-tool-selection.component';
import {InvitationStatusTagComponent} from './components/columns/invitation-status-tag/invitation-status-tag.component';
import {SendInvitationButtonComponent} from './components/send-invitation-button/send-invitation-button.component';
import {MaxParticipantsDialogComponent} from '../../dialog/max-participants-dialog/max-participants-dialog.component';
import {ProjectStepStateService} from '../../../../pages/customer/project-sequence/project-step-state.service';
import {TenderPhaseUtils} from '../../../../phase/tender-phase.util';
import {LoadingComponent} from '../../loading/loading.component';
import {NoDataComponent} from '../../no-data/no-data.component';
import {TableColumnFilterComponent} from '../table-column-filter/table-column-filter.component';
import {NgTemplateOutlet} from '@angular/common';

@Component({
  selector: 'app-invitation-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoPipe,
    TableModule,
    Tooltip,
    TimeframeDisplayComponent,
    ParticipatingToolSelectionComponent,
    InvitationStatusTagComponent,
    SendInvitationButtonComponent,
    MaxParticipantsDialogComponent,
    LoadingComponent,
    TableColumnFilterComponent,
    NoDataComponent,
    NgTemplateOutlet,
    ModelFieldSelectComponent,
    ModelFieldTextComponent,
  ],
  templateUrl: './invitation-table.component.html',
})
export class InvitationTableComponent<T extends BaseParticipatingTool = BaseParticipatingTool> {
  private readonly participatingToolService = inject(ParticipatingToolControllerService);
  private readonly projectCharterController = inject(ProjectCharterControllerService);
  private readonly stepState = inject(ProjectStepStateService);
  private readonly translocoService = inject(TranslocoService);

  readonly phaseConfig = input.required<PhaseInvitationConfig<T>>();
  readonly projectId = input.required<number>();

  readonly currentPhaseChange = output<TenderPhaseDetailsResponse | null>();

  protected readonly maxParticipantsDialogVisible = signal(false);

  // Loaded first; the heavy data below waits until this has resolved.
  private readonly previousPhaseFinishedResource = rxResource({
    params: () => ({projectId: this.projectId(), phase: this.phaseConfig().phaseType}),
    stream: ({params: request}) =>
      this.participatingToolService.checkPreviousPhaseFinished(request).pipe(
        map((response) => response.isPreviousPhaseFinished),
        catchError(() => of(false)),
      ),
  });

  // Tools + currentPhase + maxAllowed; patched in place and reloaded after invite.
  // Loads in parallel with the time-frame once the previous-phase check resolves.
  private readonly toolsResource = rxResource({
    params: () =>
      this.previousPhaseFinishedResource.hasValue()
        ? {projectId: this.projectId(), phaseConfig: this.phaseConfig()}
        : undefined,
    stream: ({params: request}) =>
      request
        ? from(request.phaseConfig.dataLoader(request.projectId)).pipe(
            map((data): InvitationTableData<T> => data),
          )
        : EMPTY,
    defaultValue: emptyInvitationTableData<T>(),
  });

  // Read-only time-frame; loaded simultaneously with the tools.
  private readonly timeFrameResource = rxResource({
    params: () =>
      this.previousPhaseFinishedResource.hasValue()
        ? {projectId: this.projectId(), tfNumber: this.phaseConfig().timeFrameNumber}
        : undefined,
    stream: ({params: request}) =>
      request ? this.projectCharterController.getTimeFrames(request) : EMPTY,
  });

  protected readonly isLoading = computed(
    () =>
      this.previousPhaseFinishedResource.isLoading() ||
      this.toolsResource.status() === 'loading' ||
      this.timeFrameResource.isLoading() ||
      (this.phaseConfig().isLoadingFn?.() ?? false),
  );

  protected readonly isReadOnly = computed(() => this.phaseConfig().stepLockedFn());
  readonly toolItems = computed(() => this.toolsResource.value().tools);
  protected readonly quickFilterValue = signal<string | null>(null);
  protected readonly searchQuery = signal<string | null>(null);
  private readonly activeLanguage = toSignal(this.translocoService.langChanges$);
  protected readonly selectedQuickFilterValue = computed(
    () => this.quickFilterValue() ?? this.tableConfig().quickFilter?.options[0]?.value ?? null,
  );
  protected readonly quickFilterOptions = computed((): InvitationTableQuickFilterSelectOption[] => {
    this.activeLanguage();
    return (this.tableConfig().quickFilter?.options ?? []).map((option) => ({
      value: option.value,
      label: this.translocoService.translate(option.labelKey),
    }));
  });
  protected readonly visibleToolItems = computed(() =>
    filterToolItems(
      this.toolItems(),
      this.tableConfig().quickFilter,
      this.selectedQuickFilterValue(),
      this.searchQuery(),
    ),
  );
  readonly currentPhase = computed(() => this.toolsResource.value().currentPhase);
  protected readonly timeFrame = computed(() => this.timeFrameResource.value());
  protected readonly maxAllowedParticipants = computed(
    () => this.toolsResource.value().maxAllowedParticipants,
  );
  private readonly previousPhaseFinished = computed(
    () => this.previousPhaseFinishedResource.value() ?? null,
  );

  protected readonly tableConfig = computed(() => this.phaseConfig().tableConfig);
  protected readonly phase = computed(() => this.phaseConfig().phaseType);

  constructor() {
    effect(() => {
      const phase = this.currentPhase();
      if (phase) {
        this.currentPhaseChange.emit(phase);
      }
    });
  }

  protected readonly participantCount = computed(
    () =>
      this.toolItems().filter(
        (item) => this.getSelectionValue(item) === TenderSelection.PARTICIPANT,
      ).length,
  );

  protected readonly maxParticipantsReached = computed(
    () => this.participantCount() >= this.maxAllowedParticipants(),
  );

  protected readonly hasUninvitedParticipants = computed(() =>
    this.toolItems().some(
      (item) =>
        this.getSelectionValue(item) === TenderSelection.PARTICIPANT &&
        this.getStatusValue(item) === null,
    ),
  );

  protected readonly suggestedSubmissionDate = computed(
    () => this.toolsResource.value().calculatedPhaseSubmissionDate,
  );

  protected readonly finalTimeframeConfig = computed((): TimeframeDisplayVM => ({
    ...this.phaseConfig().timeframeConfig,
    timeFrame: this.timeFrame(),
    currentPhase: this.currentPhase(),
  }));

  protected readonly currentPhaseStatus = computed(() => {
    if (this.isLoading()) {
      return CurrentPhaseStatus.LOADING;
    }

    return this.previousPhaseFinished()
      ? CurrentPhaseStatus.SHOW_LIST
      : CurrentPhaseStatus.PREVIOUS_PHASE_NOT_FINISHED;
  });

  protected readonly shouldShowTable = computed(
    () => this.currentPhaseStatus() === CurrentPhaseStatus.SHOW_LIST,
  );

  protected readonly showPreviousPhaseNotFinishedMessage = computed(
    () => this.currentPhaseStatus() === CurrentPhaseStatus.PREVIOUS_PHASE_NOT_FINISHED,
  );

  protected getFieldValue(item: T, column: InvitationTableConfig<T>['columns'][0]): unknown {
    if (column.valueExtractor) {
      return column.valueExtractor(item);
    }

    const fieldParts = column.field.split('.');
    let value: unknown = item;
    for (const part of fieldParts) {
      value = (value as Record<string, unknown>)?.[part];
      if (value === undefined || value === null) break;
    }

    return value;
  }

  protected getCellTooltip(
    item: T,
    column: InvitationTableConfig<T>['columns'][0],
  ): string | undefined {
    return column.cellTooltip?.(item);
  }

  protected getSelectionValue(item: T): TenderSelection | null {
    return item.selection ?? null;
  }

  private getStatusValue(item: T): TenderStatus | null {
    return item.status ?? null;
  }

  // Patches a single tool in place so backend-derived updates
  updateToolItemById(toolItemId: string, updates: Partial<T>): void {
    this.toolsResource.update((current) => ({
      ...current,
      tools: current.tools.map((item) => (item.id === toolItemId ? {...item, ...updates} : item)),
    }));
  }

  async updateParticipatingToolSelection(
    toolItem: T,
    selection: TenderSelection | null,
  ): Promise<void> {
    const participatingToolRequest = {
      hrToolDirectusId: toolItem.id,
      selection: selection ?? undefined,
    } as ParticipatingToolSelectionRequest;

    const response = await firstValueFrom(
      this.participatingToolService.updateParticipatingToolSelection({
        projectId: this.projectId(),
        phase: this.phase(),
        participatingToolSelectionRequest: participatingToolRequest,
      }),
    );

    if (response) {
      const {selection: selectionValue, status: statusValue} =
        TenderPhaseUtils.getApiResponseFieldValues(response, this.phase());
      this.updateToolItemById(toolItem.id, {
        participatingToolId: response.id,
        selection: selectionValue,
        status: statusValue,
      } as Partial<T>);
    } else {
      this.updateToolItemById(toolItem.id, {
        participatingToolId: undefined,
        selection: null,
        status: undefined,
      } as Partial<T>);
    }
  }

  async changeSelection(toolItemId: string, selection: TenderSelection | null): Promise<void> {
    const toolItem = this.toolItems().find((item) => item.id === toolItemId);
    if (toolItem) {
      await this.onSelectionChangeWrapper({item: toolItem, newSelection: selection});
    }
  }

  protected async onSelectionChangeWrapper(event: {
    item: T;
    newSelection: TenderSelection | null;
  }): Promise<void> {
    const {item: toolItem, newSelection} = event;

    if (newSelection === TenderSelection.PARTICIPANT && this.maxParticipantsReached()) {
      this.maxParticipantsDialogVisible.set(true);
      this.updateToolItemById(toolItem.id, {
        selection: this.getSelectionValue(toolItem),
      } as Partial<T>);
      return;
    }

    await this.updateParticipatingToolSelection(toolItem, newSelection);
    await this.stepState.loadStepStatuses(this.projectId());
  }

  protected onSubmissionDateUpdated(): void {
    this.toolsResource.reload();
    void this.stepState.loadStepStatuses(this.projectId());
  }
}
